import { useMemo, useState } from "react";
import { CameraIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import LoadingWheel from "../../shared/LoadingWheel";
import EditListingImages from "../EditListingImages";
import usePartDetails from "../../../data/usePartDetails";
import { IPart } from "../../../models/Part";

type Props = {
  part: IPart;
};

/**
 * Unlisted table image cell. The whole tile is clickable and opens a modal
 * containing the photo manager for editing and reordering images.
 */
export default function UnlistedImageCell({ part }: Props) {
  const [open, setOpen] = useState(false);
  const { partDetails, isLoading } = usePartDetails(part.id, open);

  const fullPart = partDetails ?? part;

  const thumbnailUrl = useMemo(() => {
    const list = Array.isArray(part.images) ? part.images : [];
    const sorted = list
      .filter((i) => (i.url || i.thumbnailUrl) && !i.deleted)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (sorted.length > 0) {
      const primary = list.find((i) => i.isThumbnail === true) ?? sorted[0];
      return primary.thumbnailUrl || primary.url || null;
    }
    return part.cdnThumbnail || part.ebayThumbnail || null;
  }, [part]);

  return (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-[96vw] w-[96vw]">
        {open && (
          <div className="max-h-[90vh] overflow-y-auto">
            {isLoading && !partDetails ? (
              <div className="flex items-center justify-center py-20">
                <LoadingWheel />
              </div>
            ) : (
              <EditListingImages part={fullPart} onlyPartImages />
            )}
          </div>
        )}
      </Modal>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title="Edit & reorder images"
        aria-label="Edit and reorder images"
        className="flex h-full w-full items-center justify-center rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white p-2 shadow-sm ring-1 ring-slate-200/50 transition hover:border-primary-300 hover:ring-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="h-auto w-full max-w-none object-contain"
          />
        ) : (
          <span className="flex w-full items-center justify-center py-2">
            <CameraIcon className="h-16 w-16 text-gray-500" aria-hidden />
          </span>
        )}
      </button>
    </>
  );
}
